import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { OrderDetailEntity } from './order-detail.entity';
import { OrderEntity } from './order.entity';
import { PromoEntity } from './promo.entity';

/**
 * Source of an order-level discount application.
 *
 * Originally the table only stored promo applications (every row =
 * PROMO). Extended to be source-aware so manual order-header discounts
 * can flow through the same per-line attribution channel. Adding new
 * sources later (e.g. saved-prices override, manager-approved one-off
 * adjustment) is one enum value + one writer.
 *
 * PROMO        - written by the promo-pricing engine. fk_promo__id is set.
 * ORDER_MANUAL - distributed from `order.discount` (manual header
 *                discount) via `prorateAcrossLines`. fk_promo__id is
 *                NULL. One row per affected order detail line.
 */
export enum OrderDiscountSource {
	PROMO = 'PROMO',
	ORDER_MANUAL = 'ORDER_MANUAL',
}

// Per-line attribution of every order-level discount — promos AND
// manual header discount. Doubles as the promo redemption log (usage
// limits) and per-line attribution for future returns/RMA work.
//
// Per-line proration model (PROMO source):
//   - LINE-scope promos: 1 row per affected line, fk_order_detail__id set
//   - ORDER-scope promos: N rows (one per eligible line), fk_order_detail__id
//     set, discountAmount = the line's prorated share. All N rows share the
//     same fk_promo__id.
//   - SHIPPING-scope promos: 1 row, fk_order_detail__id = NULL
//
// ORDER_MANUAL source: one row per eligible order detail, all sharing
// the same fk_order__id. fk_promo__id is NULL. discountTypeApplied
// captures the original input shape ('$' or '%') for display.
//
// voidedDate is set when the order transitions to confirmed CANCELLED ('7'),
// or when ORDER_MANUAL rows are recomputed (prior rows are voided then
// fresh rows are inserted). CANCELLATION_REQUESTED ('10') does NOT void
// (reversible). The promo engine excludes voided rows from usage counts.
@Index('idx_opa_order', ['fkOrderId'])
@Index('idx_opa_order_detail', ['fkOrderDetailId'])
@Index('idx_opa_promo_active', ['fkPromoId', 'voidedDate'])
@Index('idx_opa_promo_client_active', ['fkPromoId', 'fkClientId', 'voidedDate'])
@Index('idx_opa_source_active', ['fkOrderId', 'discountSource', 'voidedDate'])
@Entity('order_promo_application')
export class OrderPromoApplicationEntity extends BaseEntity {
	@Column('int', { name: 'fk_order__id', unsigned: true })
	public fkOrderId!: number;

	@Column('int', {
		name: 'fk_order_detail__id',
		nullable: true,
		unsigned: true,
	})
	public fkOrderDetailId!: number | null;

	/**
	 * Nullable for ORDER_MANUAL rows. The migration that adds the
	 * `discountSource` column also alters this from NOT NULL → NULL
	 * (existing rows keep their values; they are all PROMO source).
	 */
	@Column('int', { name: 'fk_promo__id', nullable: true, unsigned: true })
	public fkPromoId!: number | null;

	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	/**
	 * Defaults to 'PROMO' in the schema so existing rows (and any
	 * code path that doesn't yet set the column explicitly) keep the
	 * legacy semantics. Writers for ORDER_MANUAL set this explicitly.
	 */
	@Column('varchar', {
		name: 'discountSource',
		length: 32,
		default: () => "'PROMO'",
	})
	public discountSource!: OrderDiscountSource;

	@Column('varchar', { name: 'appliedAtScope', length: 16 })
	public appliedAtScope!: 'LINE' | 'ORDER' | 'SHIPPING';

	@Column('varchar', { name: 'discountTypeApplied', length: 32 })
	public discountTypeApplied!: string;

	// $ amount the customer-facing price was reduced by for this line
	// via this promo. (15,4) so cent-or-finer-precision tenants can
	// store exact values rather than pre-rounding. Reports aggregate
	// this directly: SUM(discountAmount) GROUP BY fkPromoId.
	@Column('decimal', {
		name: 'discountAmount',
		precision: 15,
		scale: 4,
	})
	public discountAmount!: string;

	// $ amount the line's COST was reduced by for this line via this
	// promo (vendor-funded portion). 0 for purely merchant-funded
	// promos (default). Used for true GP-impact: the merchant's real
	// margin loss on a line is `discountAmount - costAdjustment`.
	// Same (15,4) precision rationale as discountAmount.
	@Column('decimal', {
		name: 'costAdjustment',
		precision: 15,
		scale: 4,
		default: () => "'0'",
	})
	public costAdjustment!: string;

	@Column('varchar', { name: 'couponCodeUsed', nullable: true, length: 64 })
	public couponCodeUsed!: string | null;

	@Column('datetime', { name: 'appliedDate' })
	public appliedDate!: Date;

	@Column('datetime', { name: 'voidedDate', nullable: true })
	public voidedDate!: Date | null;

	@ManyToOne(() => OrderEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_order__id', referencedColumnName: 'id' }])
	public fkOrder!: OrderEntity;

	@ManyToOne(() => OrderDetailEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_order_detail__id', referencedColumnName: 'id' }])
	public fkOrderDetail!: OrderDetailEntity | null;

	@ManyToOne(() => PromoEntity, (promo) => promo.applications, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_promo__id', referencedColumnName: 'id' }])
	public fkPromo!: PromoEntity | null;

	@ManyToOne(() => ClientEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;
}
